import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { from, Observable, of, throwError } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'

import { SnackBarService } from '@app/core/modules/snack-bar/snack-bar.service'
import { parseRangeLimitError } from '@core/helpers/range-limit-error'

import { SHOW_FEEDBACK } from './feedback-context'

/**
 * L30-2478: api-service refuses over-long reads with a 400 whose message names the cap (32 days
 * for a GPS trail, 366 days for a report, 10,000 ids per list — backend L30-2360). Nothing in the
 * app read that 400: the map Routes tab spun forever and report tables went blank. This turns
 * those rejections into one translated snackbar for every caller and always rethrows, so each
 * screen still runs its own error path. A request opted into FormFeedbackInterceptor already
 * toasts the raw backend message, so it is left alone to avoid a double toast.
 */
@Injectable()
export class RangeLimitInterceptor implements HttpInterceptor {
  constructor(
    private readonly snackBar: SnackBarService,
    private readonly translate: TranslateService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(SHOW_FEEDBACK)) {
      return next.handle(request)
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error)
        }
        // A pdf export asks for responseType 'blob', so its 400 body arrives as a Blob holding the
        // JSON text. Reading it is asynchronous; the original error is rethrown either way, and a
        // Blob that cannot be read just means no toast.
        if (error.status === 400 && error.error instanceof Blob) {
          return from(error.error.text()).pipe(
            catchError(() => of(null)),
            switchMap((text) => {
              this.notify(error.status, text)
              return throwError(() => error)
            }),
          )
        }
        this.notify(error.status, error.error)
        return throwError(() => error)
      }),
    )
  }

  private notify(status: number, body: unknown): void {
    const limit = parseRangeLimitError(status, body)
    if (!limit) return

    const message =
      limit.kind === 'dateRange'
        ? this.translate.instant('ERRORS.DATE_RANGE_TOO_LONG', { days: limit.days })
        : this.translate.instant('ERRORS.TOO_MANY_IDS', { count: limit.count })
    this.snackBar.error(message)
  }
}
